import { CloudUploadHifiIcon, UploadCloudOutlineIcon } from '@applyboard/ui-icons';
import { FileDropItem, useDrop } from '@react-aria/dnd';
import React, { useRef } from 'react';
import {
  Box,
  ButtonProps,
  FieldCompatibleProps,
  FileList,
  Flex,
  FlexProps,
  HelpText,
  Text,
} from '~/components';
import { createFocusRescue } from '../FocusRescue';
import { useFullTheme } from '~/design';
import { InternalButton, Label, SrOnly } from '~/internal';
import { findChildOfType, shortUUID, useId, useCheckIsMounted } from '~/utils';

interface FileUploadContextProps
  extends Pick<
    FileUploadProps,
    'allowedFileTypes' | 'disabled' | 'helpText' | 'maxFileSize' | 'multiple' | 'value'
  > {
  ariaDescribedBy?: string;
  helpTextId: string;
  onChange: (value: File[]) => void;
  removeFile: (value: FileInfo) => void;
}

const FileUploadContext = React.createContext<FileUploadContextProps | null>(null);

export interface FileInfo {
  errorMessage?: string;
  warningMessage?: string;
  uploading?: boolean;
  file: File;
  id: string;
}

export interface FileUploadProps extends FieldCompatibleProps<FileInfo[]> {
  /** A list of file extensions that are allowed to be uploaded. If not supplied, all file types are valid. */
  allowedFileTypes?: string[];

  /**
   * Accepts the ID of an element on the screen with text that describes
   * the file upload.
   */
  'aria-describedby'?: string;

  /**
   * Accepts the ID of an element on the screen that can label the file
   * upload. Use this prop if the built in `label`does not match your desired UI/UX
   */
  'aria-labelledby'?: string;

  /** Whether or not the input can be interacted with. */
  disabled?: boolean;

  /** The button to render. */
  children?: React.ReactElement<FileUploadButtonProps>;

  /** The max file size in bytes. If set to 0, there is no max size. (default: 0) */
  maxFileSize?: number;

  /** The max number of files that can be uploaded. (default: undefined) */
  maxNumberOfFiles?: number;

  /** If true, multiple files can be uploaded. (default: false) */
  multiple?: boolean;

  /** Can be used if you want to be notified specifically when a file is removed.  Typically
   * onChange can be used instead */
  onRemoveFile?: (file: FileInfo) => void;

  /** The size of the file upload area. */
  size?: 'sm' | 'lg';

  /** Use this when you want to upload while the user is adding files, and not wait until the form
   * is submitted. */
  uploadFile?: (
    file: File,
    params: {
      onSuccess: (fileId: string) => void;
      onError: () => void;
      onWarning: ({ warningMessage, fileId }: { warningMessage: string; fileId: string }) => void;
    },
  ) => void;
}

const FocusRescue = createFocusRescue();

/**
 * FileUpload is a component that allows users to drag and drop one or more files, which can then
 * be submitted to an API.
 */
export function FileUpload({
  allowedFileTypes = [],
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
  children,
  disabled,
  helpText,
  invalid,
  label,
  maxFileSize = 0,
  maxNumberOfFiles,
  multiple = false,
  onBlur,
  onChange,
  onRemoveFile,
  required,
  size = 'lg',
  uploadFile,
  value = [],
}: FileUploadProps) {
  const theme = useFullTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const checkIsMounted = useCheckIsMounted();
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  const buttonChild = findChildOfType(children, FileUpload.Button);

  const fileListChild = findChildOfType(children, FileUpload.FileList);

  const labelId = useId();
  const helpTextId = useId();

  if (ariaLabelledBy && label) {
    throw new Error('Only one of aria-labelledby or label can be provided.');
  }

  const onChangeHelper = (fileList: File[]) => {
    const newValues: FileInfo[] = [];

    fileList.forEach((file, index) => {
      const fileValue: FileInfo = {
        file,
        id: shortUUID(),
      };

      if (maxFileSize !== 0) {
        if (file.size > maxFileSize) {
          fileValue.errorMessage = "Can't upload this file because it exceeds the size limit.";
        }
      }
      if ((allowedFileTypes ?? []).length > 0) {
        const allowedFileTypesFormatted = allowedFileTypes.map(t => t.toLowerCase());
        if (
          !allowedFileTypesFormatted.find(fileExtension =>
            file.name.toLowerCase().endsWith(fileExtension),
          )
        ) {
          fileValue.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${allowedFileTypesFormatted.join(
            ', ',
          )}`;
        }
      }

      if (maxNumberOfFiles && value.length + index + 1 > maxNumberOfFiles) {
        fileValue.errorMessage = `Can't upload more than ${maxNumberOfFiles} files.`;
      }

      // If uploadFile is provided by the consumer, and there was no validation
      // error, then we should try to upload the file immediately instead of
      // waiting for the consumer to do it themselves, typically on form submit
      // Additionally, if multiple = false, only trigger uploadFile for first
      // File, to match what is being passed to onChange below
      if (uploadFile && !fileValue.errorMessage && (multiple || index < 1)) {
        fileValue.uploading = true;
        uploadFile(fileValue.file, {
          onSuccess: fileId => {
            // Do not try calling onChange if the component has unmounted since the file was added
            if (!checkIsMounted()) return;

            // We need to use refs here because of complications with closures
            onChangeRef.current(
              valueRef.current.map(item => {
                if (item.id === fileValue.id) {
                  return {
                    ...item,
                    id: fileId,
                    uploading: false,
                  };
                }

                return item;
              }),
            );
          },
          onError: (errorMessage?: string) => {
            // Do not try calling onChange if the component has unmounted since the file was added
            if (!checkIsMounted()) return;

            // We need to use refs here because of complications with closures
            onChangeRef.current(
              valueRef.current.map(item => {
                if (item.id === fileValue.id) {
                  return {
                    ...item,
                    uploading: false,
                    errorMessage: errorMessage || 'Error while trying to upload file',
                  };
                }

                return item;
              }),
            );
          },
          onWarning: ({ warningMessage, fileId }: { warningMessage: string; fileId: string }) => {
            // Do not try calling onChange if the component has unmounted since the file was added
            if (!checkIsMounted()) return;

            // We need to use refs here because of complications with closures
            onChangeRef.current(
              valueRef.current.map(item => {
                if (item.id === fileValue.id) {
                  return {
                    ...item,
                    id: fileId,
                    uploading: false,
                    warningMessage,
                  };
                }

                return item;
              }),
            );
          },
        });
      }

      newValues.push(fileValue);
    });

    if (!multiple) {
      onChange(newValues.slice(0, 1));
    } else {
      onChange([...value, ...newValues]);
    }
  };

  const removeFile = (f: FileInfo) => {
    const newValue = value.filter(otherF => otherF.id !== f.id);
    onChange(newValue);
    onRemoveFile?.(f);
  };

  return (
    <FileUploadContext.Provider
      value={{
        allowedFileTypes,
        ariaDescribedBy,
        disabled,
        helpText,
        helpTextId,
        multiple,
        onChange: onChangeHelper,
        removeFile,
        value,
      }}
    >
      <FocusRescue>
        {/* This is fine to disable because we're reacting to blurs from child interactive elements */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <fieldset
          aria-labelledby={ariaLabelledBy || labelId}
          css={{
            border: 0,
            marginInline: 0,
            padding: 0,
            width: '100%',
          }}
          onBlur={event => {
            const nextFocusedEl = event.relatedTarget;
            if (nextFocusedEl === null) {
              // I _think_ we can use this conditional to determine when the file dialog is opened
              return;
            }

            const fieldsetEl = event.currentTarget;
            const focusStillInFieldset =
              fieldsetEl &&
              nextFocusedEl instanceof Element &&
              nextFocusedEl.closest('fieldset') === fieldsetEl;

            if (!focusStillInFieldset) {
              onBlur?.();
            }
          }}
        >
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing[2],
              marginBottom: value.length ? theme.spacing[4] : 0,
              width: '100%',
            }}
            ref={containerRef}
          >
            <Label
              disabled={disabled}
              id={labelId}
              label={label}
              overflow="normal"
              required={required}
            />

            <FileUploadBox size={size}>
              <FocusRescue.Rescuer>
                {buttonChild ?? <FileUpload.Button>Browse Files</FileUpload.Button>}
              </FocusRescue.Rescuer>
            </FileUploadBox>

            {helpText && (
              <HelpText
                id={helpTextId}
                intent={invalid ? 'negative' : 'secondary'}
                showIcon={invalid}
              >
                {helpText}
              </HelpText>
            )}
          </div>

          <FocusRescue.Rescuee>
            {fileListChild ?? (
              <FileList maxItems={4} orientation="vertical">
                {value.map(fileInfo => (
                  <FileList.Item
                    helpText={fileInfo.errorMessage ?? fileInfo.warningMessage}
                    intent={
                      fileInfo.errorMessage
                        ? 'negative'
                        : fileInfo.warningMessage
                          ? 'warning'
                          : 'secondary'
                    }
                    key={fileInfo.id}
                    name={fileInfo.file.name}
                    onRemove={() => removeFile(fileInfo)}
                    uploading={fileInfo.uploading}
                    value={fileInfo.id}
                  />
                ))}
              </FileList>
            )}
          </FocusRescue.Rescuee>
        </fieldset>
      </FocusRescue>
    </FileUploadContext.Provider>
  );
}

interface FileUploadBoxProps extends Pick<FileUploadProps, 'size'> {
  children: React.ReactElement<FileUploadButtonProps>;
}

function FileUploadBox({ children, size = 'lg' }: FileUploadBoxProps) {
  const theme = useFullTheme();
  const context = React.useContext(FileUploadContext);
  if (!context) {
    throw new Error('Missing context');
  }

  const ref = React.useRef(null);
  const { dropProps, isDropTarget } = useDrop({
    ref,
    async onDrop(e) {
      const result = await Promise.all(
        e.items.filter(item => item.kind === 'file').map(item => (item as FileDropItem).getFile()),
      );
      context.onChange(result);
    },
  });

  const sizeStyle = (
    {
      lg: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: theme.spacing[6],
      },
      sm: {
        alignItems: 'center',
        display: 'flex',
        padding: theme.spacing[4],
      },
    } as const
  )[size];

  const dropStyle = {
    ...theme.borders.primary,
    backgroundColor: theme.colors.bgPrimary,
    backgroundImage: 'unset',
    // Because we're using a background image as the border for the default state, adding a border
    // during the drop state will cause a content shift downwards.
    marginTop: '-1px',
  };

  const isLarge = size === 'lg';
  const ButtonContainerTag = isLarge ? React.Fragment : Flex;
  const buttonContainerProps = isLarge
    ? {}
    : ({
        align: 'center',
        direction: {
          xs: 'column',
          sm: 'row',
        },
        gap: {
          xs: 1,
          sm: 0,
        },
        grow: 1,
        justify: 'between',
      } as FlexProps);

  const dashedBorderStroke = encodeURIComponent(theme.colors.borderSecondary);

  return (
    <div
      {...dropProps}
      css={[
        {
          backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='16' ry='16' stroke='${dashedBorderStroke}' stroke-width='2' stroke-dasharray='8' stroke-dashoffset='8' stroke-linecap='butt'/%3e%3c/svg%3e")`,
          borderRadius: theme.spacing[4],
          gap: theme.spacing[4],
          width: '100%',
        },
        isDropTarget && dropStyle,
        sizeStyle,
      ]}
      ref={ref}
    >
      <Box hideBelow="sm">
        {isLarge ? (
          <CloudUploadHifiIcon intent="secondary" />
        ) : (
          <UploadCloudOutlineIcon intent="secondary" />
        )}
      </Box>

      <ButtonContainerTag {...buttonContainerProps}>
        <Text>Drag and drop, or</Text>
        {children}
      </ButtonContainerTag>
    </div>
  );
}

export type FileUploadButtonProps = Pick<
  ButtonProps,
  'aria-label' | 'children' | 'disabled' | 'emphasis' | 'intent'
>;

FileUpload.Button = function FileUploadButton({
  children,
  disabled,
  emphasis = 'outlined',
  intent = 'primary',
}: FileUploadButtonProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const inputId = useId();
  const context = React.useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUpload.Button can only be used inside FileUpload');
  }

  const isDisabled = context.disabled ?? disabled;
  return (
    <>
      <InternalButton
        disabled={isDisabled}
        emphasis={emphasis}
        intent={intent}
        onClick={() => inputRef?.current?.click()}
        aria-describedby={`${context.helpText ? context.helpTextId : ''} ${context.ariaDescribedBy || ''}`}
      >
        {children}

        <SrOnly>(Click to add files)</SrOnly>
      </InternalButton>

      <input
        accept={context.allowedFileTypes?.join(',') ?? undefined}
        css={{ display: 'none' }}
        data-testid="cds-file-upload"
        disabled={isDisabled}
        id={inputId}
        multiple={context.multiple ?? false}
        onChange={e => context.onChange(Array.from(e.target.files ?? []))}
        ref={inputRef}
        type="file"
        value=""
      />
    </>
  );
};

FileUpload.FileList = function FileUploadFileList(props: { children: React.ReactNode }) {
  const context = React.useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUpload.FileList can only be used inside FileUpload');
  }

  return <>{props.children}</>;
};

export const validateFiles = (files: FileInfo[], maxNumberOfFiles = 0) => {
  if (maxNumberOfFiles !== 0) {
    if (files.length > maxNumberOfFiles) {
      return `Can't upload more than ${maxNumberOfFiles} files.`;
    }
  }

  if (files.find(f => !!f.errorMessage)) {
    return `There's an issue with one of the files you've added.`;
  }

  if (files.find(f => !!f.uploading)) {
    return `Some of the files are still uploading.`;
  }

  return true;
};
